import { Component, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest } from "rxjs";
import { AppState } from "src/app/shared/models/store models/app-state.interface";
import { BulkDataOpsService } from "src/app/shared/services/bulk-data-ops.service";
import { ToastService } from "src/app/shared/services/toast.service";
import { getACLicenseStatus, getLTLicenseStatus } from "src/app/state";

@Component({
  selector: "app-data-export",
  templateUrl: "./data-export.component.html",
  styleUrls: ["./data-export.component.scss"],
})
export class DataExportComponent implements OnInit {
  public selectableEntities = [
    { sortorder: 1, name: "Users", provisioningTypeId: 7 },
    { sortorder: 2, name: "User Availability", provisioningTypeId: 5 },
    { sortorder: 3, name: "User Skills", provisioningTypeId: 6 },
    { sortorder: 4, name: "Locations", provisioningTypeId: 1 },
    { sortorder: 5, name: "Operating Hours", provisioningTypeId: 2 },
    { sortorder: 6, name: "Holidays", provisioningTypeId: 0 },
    { sortorder: 7, name: "Product Categories", provisioningTypeId: 3 },
    { sortorder: 8, name: "Products & Services", provisioningTypeId: 4 },
  ];

  fileTypeSelected: number = this.selectableEntities[0].provisioningTypeId;

  constructor(
    private bulkDataOpsService: BulkDataOpsService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    combineLatest([
      this.store.select(getLTLicenseStatus),
      this.store.select(getACLicenseStatus),
    ]).subscribe(([isLTLicenseEnabled, isACLicenseEnabled]) => {
      this.selectableEntities = this.getEntitiesForLicense(
        isLTLicenseEnabled,
        isACLicenseEnabled,
      );
      this.fileTypeSelected =
        this.selectableEntities[0]?.provisioningTypeId ?? this.fileTypeSelected;
      this.setTranslatedNames();
    });
  }

  private readonly translationKeysById: Record<number, string> = {
    7: "_General_Users_Text_",
    5: "_Data_Import_User_Availability_Text_",
    6: "_Data_Import_User_Skills_Text_",
    1: "_General_Locations_Text_",
    2: "_Location_Operating_Hours_Tab_Title_Text_",
    0: "_Location_Holidays_Tab_Title_Text_",
    3: "_General_Product_Categories_Text_",
    4: "_General_Products_Services_Text_",
  };

  private getEntitiesForLicense(isLT: boolean, isAC: boolean) {
    const all = [
      { sortorder: 1, name: "Users", provisioningTypeId: 7 },
      { sortorder: 2, name: "User Availability", provisioningTypeId: 5 },
      { sortorder: 3, name: "User Skills", provisioningTypeId: 6 },
      { sortorder: 4, name: "Locations", provisioningTypeId: 1 },
      { sortorder: 5, name: "Operating Hours", provisioningTypeId: 2 },
      { sortorder: 6, name: "Holidays", provisioningTypeId: 0 },
      { sortorder: 7, name: "Product Categories", provisioningTypeId: 3 },
      { sortorder: 8, name: "Products & Services", provisioningTypeId: 4 },
    ];
    // LT only -> Users, Locations, Product Categories, Products & Services
    if (isLT && !isAC) {
      return all
        .filter((e) => [7, 1, 3, 4].includes(e.provisioningTypeId))
        .map((e, idx) => ({ ...e, sortorder: idx + 1 }));
    }
    // AC only or both AC & LT -> full list
    return all;
  }

  private setTranslatedNames() {
    const keys = Object.values(this.translationKeysById);
    this.translateService.get(keys).subscribe((res) => {
      this.selectableEntities = this.selectableEntities.map((e) => ({
        ...e,
        name: res[this.translationKeysById[e.provisioningTypeId]] ?? e.name,
      }));
    });
  }

  onClickExport() {
    console.log(this.fileTypeSelected, this.selectableEntities);
    if (this.selectableEntities && this.selectableEntities.length > 0) {
      const selectedEntity = this.selectableEntities.find(
        (e) => e.provisioningTypeId == this.fileTypeSelected,
      );
      this.exportToCSV(this.fileTypeSelected, selectedEntity?.name ?? "");
    }
  }

  exportToCSV(id: number, fileTypeName: string) {
    this.bulkDataOpsService.exportToCSV(id).subscribe({
      next: ({ blob, filename }) => {
        // Format filename to OneCXLocations_M-D-YYYY
        let newFilename = filename;
        const match = filename.match(/^(.*_)(\d{1,2})(\d{2})(\d{4})(.*)$/);
        if (match) {
          const month = parseInt(match[2], 10);
          const day = parseInt(match[3], 10);
          newFilename = `${match[1]}${month}-${day}-${match[4]}${match[5]}`;
        }
        const successMessage = this.translateService.instant(
          "_Export_Success_Message_",
        );
        this.toastService.showSuccess(`${fileTypeName} ${successMessage}`);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = newFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        const errorMessage = this.translateService.instant(
          "_Export_Fail_Message_",
        );
        this.toastService.showError(`${fileTypeName} ${errorMessage}`);
      },
    });
  }
}
